import { memo, useMemo } from 'react'
import Skeleton, { SkeletonTheme } from 'react-loading-skeleton'
import { useParams } from 'react-router'

import {
  CategoryChildrens,
  CategoryParent,
} from '@entities/Categories'

import { useAddressStore } from '@features/shipping-methods'


import { IMAGE_SIZES } from '@shared/contants'
import { useCategoriesBranchQuery } from '@shared/generated'
import { cn } from '@shared/lib/cn'
import Accordion from '@shared/ui/accordion'

import styles from './styles.module.scss'

interface CategoriesBranchProps {
  className?: string;
}

// Мемоизированный компонент загрузки
const CategoriesBranchLoading = memo(function CategoriesBranchLoading () {
  return (
    <SkeletonTheme>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          flexWrap: 'wrap',
        }}
      >
        {Array.from({ length: 12 }).map((_, index) => (
          <div
            key={index}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
            }}
          >
            <Skeleton
              count={1}
              width={32}
              height={32}
              style={{ borderRadius: 8 }}
            />
            <Skeleton
              count={1}
              width={index % 2 === 0 ? 130 : 100}
              height={16}
              style={{ borderRadius: 6 }}
            />
          </div>
        ))}
      </div>
    </SkeletonTheme>
  )
})

// Мемоизированный основной компонент
const CategoriesBranch = memo(function CategoriesBranch (
  props: CategoriesBranchProps,
) {
  const { categorySlug } = useParams()
  const { className } = props
  const { shopId } = useAddressStore()

  // Запрос данных
  const { data, loading } = useCategoriesBranchQuery({
    variables: {
      shopId: shopId,
      style: IMAGE_SIZES.SMALL,
    },
  })

  // Мемоизация списка категорий
  const categories = useMemo(() => {
    return data?.categoriesBranch || []
  }, [ data?.categoriesBranch ])

  // Мемоизация рендеринга элементов
  const renderedItems = useMemo(() => {
    return categories.map((item) => {
      const isOpen = categorySlug === item?.slug

      return (
        <div className={styles.root__item} key={item?.id}>
          <Accordion
            className={styles.accordion}
            triggerClassName={styles['root__parent-link']}
            trigger={
              <CategoryParent
                name={item?.name as string}
                iconUrl={item?.miniIcon?.url ?? item?.icon?.url}
              />
            }
            content={
              <CategoryChildrens
                parentSlug={item?.slug as string}
                items={item?.childrens}
              />
            }
            arrowIcon={false}
            defaultOpen={isOpen}
          />
        </div>
      )
    })
  }, [ categories, categorySlug ])

  if (loading) {
    return <CategoriesBranchLoading />
  }

  return <div className={cn(styles.root, className)}>{renderedItems}</div>
})

export default CategoriesBranch
